/**
 * Пуш-уведомления через чистый Web Push (VAPID), без Firebase.
 * enablePush вызывается только из жеста пользователя: iOS Safari требует его
 * для requestPermission().
 */
import { registerDevice } from '@/shared/auth/registerDevice';
import { getAppMode } from '@/shared/utils';
import {
  clearSentPushEndpoint,
  getSentPushEndpoint,
  isPushDisabledByUser,
  setPushDisabledByUser,
  setSentPushEndpoint,
} from './sentEndpoint';
import { store, type AppDispatch } from '@/app/store';
import { getCurrentScreen, track } from '@/shared/analytics';

/**
 * Логаут снимает с девайса токен, но userId оставляет — анонимная подписка
 * вернула бы пуши вышедшему человеку.
 */
function isLoggedIn(): boolean {
  return store.getState().login.isLoggedIn;
}

export type PushAvailability =
  | 'available' // можно подписываться
  | 'granted' // уже подписаны
  | 'denied' // пользователь запретил в браузере
  | 'off' // выключено в профиле, разрешение браузера осталось
  | 'needs-install' // iOS: пуши доступны только в установленной PWA
  | 'unsupported'; // MAX-режим / старый браузер / нет конфига

function isIos(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS маскируется под macOS
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari-специфичный флаг
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

export async function getPushAvailability(): Promise<PushAvailability> {
  if (getAppMode() !== 'pwa') return 'unsupported';
  if (!window.__CONFIG__?.vapidPublicKey) return 'unsupported';
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    // iOS Safari до установки на экран не даёт Push API вовсе
    if (isIos() && !isStandalone()) return 'needs-install';
    return 'unsupported';
  }
  if (isIos() && !isStandalone()) return 'needs-install';

  switch (Notification.permission) {
    case 'granted':
      return isPushDisabledByUser() ? 'off' : 'granted';
    case 'denied':
      return 'denied';
    default:
      return 'available';
  }
}

/** applicationServerKey должен быть Uint8Array, а не base64url-строкой */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

async function subscribe(
  registration: ServiceWorkerRegistration,
  applicationServerKey: Uint8Array
): Promise<PushSubscription> {
  try {
    return await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  } catch (error) {
    // InvalidStateError: подписка оформлена под другим VAPID-ключом
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      await existing.unsubscribe();
      return registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    }
    throw error;
  }
}

async function registerPushSubscription(
  dispatch: AppDispatch,
  force = false
): Promise<void> {
  const vapidPublicKey = window.__CONFIG__?.vapidPublicKey;
  if (!vapidPublicKey)
    throw new Error('[push] vapidPublicKey не задан в __CONFIG__');

  const registration = await navigator.serviceWorker.ready;
  const subscription = await subscribe(
    registration,
    urlBase64ToUint8Array(vapidPublicKey)
  );

  // По явному действию человека отправляем всегда: на бэке записи может уже
  // не быть
  if (!force && subscription.endpoint === getSentPushEndpoint()) return;

  await registerDevice(dispatch, {
    pushToken: JSON.stringify(subscription.toJSON()),
    pushProvider: 'WEBPUSH',
  });

  setSentPushEndpoint(subscription.endpoint);
}

/** Тихая инициализация при старте: ничего не спрашивает у пользователя */
export async function initPush(dispatch: AppDispatch): Promise<void> {
  try {
    if (!isLoggedIn()) return;

    const availability = await getPushAvailability();
    if (availability !== 'granted') return;

    await registerPushSubscription(dispatch);
  } catch (error) {
    console.warn('[push] не удалось инициализировать пуши', error);
  }
}

/**
 * Сверяет подписку при возвращении в приложение: браузер меняет endpoint сам,
 * а на iOS pushsubscriptionchange не приходит вовсе.
 */
export function watchPushSubscription(dispatch: AppDispatch): void {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void initPush(dispatch);
  });

  // На старте initPush вышел без сессии, а разрешение могло остаться с прошлой
  let loggedIn = isLoggedIn();
  store.subscribe(() => {
    const now = isLoggedIn();
    if (now && !loggedIn) void initPush(dispatch);
    loggedIn = now;
  });
}

/**
 * Подписка по явному действию пользователя.
 * @returns true, если подписка оформлена и доехала до бэка
 * @throws если подписаться не удалось — например, браузер без push-сервиса
 */
export async function enablePush(dispatch: AppDispatch): Promise<boolean> {
  if (!isLoggedIn()) return false;

  const availability = await getPushAvailability();
  if (availability === 'denied' || availability === 'needs-install')
    return false;
  if (availability === 'unsupported') return false;

  if (availability === 'available') {
    const permission = await Notification.requestPermission();
    // default — окно закрыли без ответа, это не решение
    if (permission !== 'default') {
      track(
        permission === 'granted'
          ? 'push_permission_granted'
          : 'push_permission_denied',
        { source: getCurrentScreen() }
      );
    }
    if (permission !== 'granted') return false;
  }

  setPushDisabledByUser(false);
  await registerPushSubscription(dispatch, true);

  return true;
}

/** Разрешение браузера не трогаем: включить обратно можно без системного окна */
export async function disablePush(dispatch: AppDispatch): Promise<void> {
  setPushDisabledByUser(true);
  clearSentPushEndpoint();

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) await subscription.unsubscribe();

  await registerDevice(dispatch, { pushTokenIsExpired: true });
}
